import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Skeleton, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController.ts';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations.ts';
import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { HoldingsCardStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/HoldingsCard.styles.tsx';
import {
	PersistedViewKey,
	usePersistedCardTableView,
} from '@dealstack/frontend/shared/components/CardTableViewSelector/usePersistedCardTableView.ts';
import { HoldingsCardActions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/HoldingsCardActions.tsx';
import { InvestorHoldingRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.types.ts';
import { HoldingVestingDetail } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/HoldingTable/HoldingVestingDetail.tsx';
import { HoldingsCards } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/HoldingsCards.tsx';
import { getInvestorHoldingsTableColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldingsTable.utils.tsx';
import { InvestorDetailsEmptyCardState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.tsx';
import { EMPTY_CARD_STATE_HEIGHT } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.styles.tsx';
import { HoldingsCardsSkeleton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/HoldingsCardsSkeleton.tsx';
import { canExpandVesting } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.utils.ts';
import { CardTableView } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector.types.ts';

interface Props {
	dealId: string;
	rows: InvestorHoldingRow[];
	isLoading: boolean;
	isError: boolean;
	viewStorageKey: PersistedViewKey;
	heldColumn?: 'via' | 'for';
	holderFilterName?: string;
	onOpenTransactions?: (row: InvestorHoldingRow) => void;
}

export const HoldingsSection = ({
	dealId,
	rows,
	isLoading,
	isError,
	viewStorageKey,
	heldColumn = 'via',
	holderFilterName,
	onOpenTransactions,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const getSecurityTypeTranslation = useSecurityTypeTranslation();
	const dataOperations = useMantineTableDataOperations();
	const defaultView: CardTableView = rows.length > 2 ? 'table' : 'cards';

	const [view, setView, hasChosenView] = usePersistedCardTableView(viewStorageKey, defaultView);
	const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
		totalCostAverage: false,
		costPerUnitAverage: false,
		costPerUnitActual: false,
	});
	const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);

	const isCardView = view === 'cards';
	const hasTable = !isCardView && !isLoading && !isError && rows.length > 0;

	const directlyLabel = tMipManage('investors.details.holdings_table.held_directly');

	const columns = useMemo(
		() =>
			getInvestorHoldingsTableColumns({
				tMipManage,
				getSecurityTypeTranslation,
				directlyLabel,
				heldColumn,
				onOpenTransactions,
			}),
		[tMipManage, getSecurityTypeTranslation, directlyLabel, heldColumn, onOpenTransactions]
	);

	const singleIssuerId = useMemo(() => {
		const issuerIds = new Set(rows.map((row) => row.issuerId));
		return issuerIds.size === 1 ? rows[0]?.issuerId ?? null : null;
	}, [rows]);

	const renderContent = () => {
		if (isLoading) {
			// Without a stored choice the view depends on the row count, unknown until the rows load.
			if (isCardView && hasChosenView) {
				return <HoldingsCardsSkeleton />;
			}

			return <Skeleton height={EMPTY_CARD_STATE_HEIGHT} radius="0.5rem" />;
		}

		if (isError) {
			return (
				<InvestorDetailsEmptyCardState
					icon="es-pie-chart"
					title={tMipManage('investors.details.holdings_table.error_title')}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDINGS_ERROR}
				/>
			);
		}

		if (!rows.length) {
			return (
				<InvestorDetailsEmptyCardState
					icon="es-pie-chart"
					title={tMipManage('investors.details.holdings_table.empty_title')}
					description={tMipManage('investors.details.holdings_table.empty_description')}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDINGS_EMPTY}
				/>
			);
		}

		if (isCardView) {
			return (
				<HoldingsCards
					rows={rows}
					getSecurityTypeTranslation={getSecurityTypeTranslation}
					heldColumn={heldColumn}
				/>
			);
		}

		return (
			<MantineTable
				columns={columns}
				data={rows}
				dataOperations={dataOperations}
				manualFiltering={false}
				manualSorting={false}
				enablePagination={false}
				minWidth={80}
				enableColumnResizing={true}
				pinnedColumnIds={['asset']}
				state={{ columnVisibility }}
				onColumnVisibilityChange={setColumnVisibility}
				enableExpanding
				// Vesting belongs to the investor the units are held for, which each row carries.
				getRowCanExpand={(row) => canExpandVesting(row.original)}
				renderDetailPanel={({ row }) =>
					row.original.heldForInvestorId ? (
						<HoldingVestingDetail
							investorId={row.original.heldForInvestorId}
							securityId={row.original.securityId}
						/>
					) : null
				}
				renderTopToolbar={({ table }) => {
					// Full screen makes the table a fixed overlay, so the header slot is off
					// screen — render inline then, showing just the exit control.
					const { isFullScreen } = (table.options.meta ?? {}) as {
						isFullScreen?: boolean;
					};

					const actions = (
						<HoldingsCardActions
							table={table}
							dealId={dealId}
							singleIssuerId={singleIssuerId}
							holderFilterName={holderFilterName}
							view={view}
							onViewChange={setView}
							isFullScreen={isFullScreen}
						/>
					);

					if (isFullScreen || !actionsSlot) {
						return actions;
					}

					return createPortal(actions, actionsSlot);
				}}
			/>
		);
	};

	return (
		<HoldingsCardStyles
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDINGS_CARD}
		>
			<InvestmentDetailsCard
				cardLabelComponent={
					<Text fw={600} c="dark.8" className="label">
						{tMipManage('investors.details.holdings_table.title')}
					</Text>
				}
				actions={
					isCardView || !hasTable ? (
						<HoldingsCardActions
							dealId={dealId}
							singleIssuerId={singleIssuerId}
							holderFilterName={holderFilterName}
							view={view}
							onViewChange={setView}
						/>
					) : (
						<div ref={setActionsSlot} />
					)
				}
				variant="borderless"
			>
				{renderContent()}
			</InvestmentDetailsCard>
		</HoldingsCardStyles>
	);
};
